import { Injectable, inject } from '@angular/core';
import { DatePipe, CurrencyPipe } from '@angular/common';

interface ValueControl {
  value: string | number | null | undefined;
  setValue(value: unknown, options?: { emitEvent?: boolean }): void;
}

@Injectable({ providedIn: 'root' })
export class FormattingUtilsService {
  private currencyPipe = inject(CurrencyPipe);
  private datePipe = inject(DatePipe);

  formatDate(value: string | number | Date | null | undefined, format?: string, timezone?: string, locale?: string): string | null {
    if (value == null) {
      return null;
    }

    return this.datePipe.transform(value, format, timezone, locale ?? 'nl');
  }

  formatMoney(value: string | number): string {
    let newValue = value;
    if (typeof value === 'number') {
      newValue = value.toString();
    }

    const transformedValue = this.currencyPipe.transform(String(newValue).replace(/,/g, ''));
    newValue = transformedValue ? transformedValue.replace('$', '') : '';
    if (newValue.startsWith('-')) {
      const transformedZeroValue = this.currencyPipe.transform('0,00'.replace(/,/g, ''));
      newValue = transformedZeroValue ? transformedZeroValue.replace('$', '') : '';
    }
    return newValue;
  }

  removeMoneyQuantity(target: ValueControl, value: number): void {
    if ((Number(target.value) - value) >= 3) {
      target.setValue(this.formatMoney(Number(target.value) - value), { emitEvent: false });
    }
  }

  addMoneyQuantity(target: ValueControl, value: number): void {
    target.setValue(this.formatMoney(Number(target.value) + value), { emitEvent: false });
  }

  removeTimeQuantity(target: ValueControl, value: number): void {
    const currentValue = this.parseTimeQuantity(target.value);
    if (!Number.isFinite(currentValue)) {
      return;
    }

    const nextValue = Math.round((currentValue - value) * 100) / 100;
    if (nextValue >= 0.5) {
      target.setValue(this.formatTimeQuantity(nextValue));
    }
  }

  addTimeQuantity(target: ValueControl, value: number): void {
    const currentValue = this.parseTimeQuantity(target.value);
    if (!Number.isFinite(currentValue)) {
      return;
    }

    const nextValue = Math.round((currentValue + value) * 100) / 100;
    target.setValue(this.formatTimeQuantity(nextValue));
  }

  calculateDate(year: number): string {
    const date = new Date(new Date().setMonth(new Date().getMonth() - (year * 12)));
    const dateNew = this.datePipe.transform(date, 'yyyy-MM-dd');
    return `${dateNew ?? ''}T00:00`;
  }

  calculateMinuteDifferenceBetweenTimestampAndNow(timestamp: unknown): number {
    let timestampConverted = 0;

    if (timestamp instanceof Date) {
      timestampConverted = timestamp.getTime();
    } else if (typeof timestamp === 'string' || typeof timestamp === 'number') {
      timestampConverted = new Date(timestamp).getTime();
    } else if (typeof timestamp === 'object' && timestamp !== null) {
      const seconds = this.readNumberField(timestamp, 'seconds') ?? 0;
      const nanoseconds = this.readNumberField(timestamp, 'nanoseconds') ?? 0;
      timestampConverted = seconds * 1000 + Math.round(nanoseconds / 1000000);
    }

    const now = new Date();
    const parsedTimestamp = new Date(timestampConverted).getTime();
    if (!Number.isFinite(parsedTimestamp)) {
      return 0;
    }

    const timeDifferenceMs = now.getTime() - parsedTimestamp;
    return Math.round(timeDifferenceMs / 60000);
  }

  private parseTimeQuantity(value: string | number | null | undefined): number {
    if (typeof value === 'number') {
      return value;
    }

    if (typeof value === 'string') {
      return Number(value.replace(',', '.'));
    }

    return Number.NaN;
  }

  private formatTimeQuantity(value: number): string {
    const roundedValue = Math.round(value * 100) / 100;
    if (Number.isInteger(roundedValue)) {
      return String(roundedValue);
    }

    return String(roundedValue).replace('.', ',');
  }

  private readNumberField(source: unknown, field: string): number | undefined {
    if (typeof source !== 'object' || source === null) {
      return undefined;
    }

    const value = Reflect.get(source, field);
    return typeof value === 'number' ? value : undefined;
  }
}
